import React from 'react';
import { Table as BTable } from 'react-bootstrap';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { FaSpinner } from 'react-icons/fa';


import './table.scss';

interface Props {
  columns: ColumnDef<any>[];
  data: any;
  onRowClick?: (data: any) => void;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  totalRowCount?: number;
  loading?: boolean;
}

const DataTable = ({ data, columns, onRowClick, pagination, onPaginationChange, totalRowCount, loading }: Props) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalRowCount ? Math.ceil(totalRowCount / (pagination?.pageSize || 10)) : -1,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination,
    },
    onPaginationChange: onPaginationChange as any,
  });

  return (
    <div className="table-wrapper">
      <BTable className="component_table" striped bordered hover responsive size="sm">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={table.getAllLeafColumns().length} className="text-center py-4">
                <FaSpinner className="table-spinner" />
                <div>Loading data...</div>
              </td>
            </tr>
          ) : table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={onRowClick ? 'cursor-pointer table-row-hover' : ''}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </BTable>
      <div className="table-footer">
        <div className="table-footer-info">
          Page <span>{table.getState().pagination.pageIndex + 1}</span> of <span>{table.getPageCount()}</span>
        </div>
        <div className="table-footer-controls">
          <button
            className="table-btn"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <button
            className="table-btn table-btn-primary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;